import React, { useState, useEffect, useMemo } from 'react';
import { Card, Button, Badge, Alert } from 'flowbite-react';
import { HiPlus, HiPencil, HiTrash, HiBriefcase, HiTag, HiExclamation } from 'react-icons/hi';
import { staffApi, type StaffAssignment, type StaffAssignmentCreateData } from '../../api/staff';
import { ApiError } from '../../api/client';
import { descriptorsApi, type Descriptor } from '../../api/descriptors';
import { leasApi } from '../../api/leas';
import { AddAssignmentModal } from '../modals/AddAssignmentModal';
import { useAuth } from '../../contexts/AuthContext';

// Helper to format date strings without timezone offset issues
// Date strings like "2024-01-15" get interpreted as UTC, causing off-by-one errors
const formatDateLocal = (dateStr: string): string => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString();
};

// Matches the backend: an assignment is current until the day it ends
const isCurrentAssignment = (a: StaffAssignment): boolean => {
  if (!a.endDate) return true;
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  return a.endDate.slice(0, 10) > todayStr;
};

interface AssignmentsManagerProps {
  staffId: number;
  assignments: StaffAssignment[];
  onUpdate: () => void;
  lockedOrganizationId?: number; // Optional: lock to specific org for org profile pages
  prioritizeBoardRoles?: boolean; // Optional: show School Board roles first in dropdown (for Board tab)
  autoOpenAddModal?: boolean; // Optional: auto-open the add modal when component mounts
  defaultRoleDescriptorId?: number; // Optional: pre-fill role in add modal (used with autoOpenAddModal)
}

export const AssignmentsManager: React.FC<AssignmentsManagerProps> = ({
  staffId,
  assignments,
  onUpdate,
  lockedOrganizationId,
  prioritizeBoardRoles = false,
  autoOpenAddModal = false,
  defaultRoleDescriptorId
}) => {
  const { currentUser } = useAuth();

  // Permission check - can user edit assignments?
  const canEdit = useMemo(() => {
    if (!currentUser) return false;
    if (currentUser.role === 'superAdmin') return true;
    if (currentUser.role === 'organizationAdmin') {
      // If locked to an org, check if user has access to that org
      if (lockedOrganizationId) {
        return currentUser.organizationIds?.includes(lockedOrganizationId) ?? false;
      }
      // Otherwise, allow if user is an org admin (they can only see their orgs anyway)
      return true;
    }
    return false;
  }, [currentUser, lockedOrganizationId]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<StaffAssignment | null>(null);
  const [defaultAssignmentType, setDefaultAssignmentType] = useState<'primary' | 'roleTag'>('primary');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [roleDescriptors, setRoleDescriptors] = useState<Descriptor[]>([]);
  const [organizations, setOrganizations] = useState<Array<{ id: number; name: string }>>([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Load reference data
  useEffect(() => {
    if (currentUser && !dataLoaded) {
      loadReferenceData();
    }
  }, [currentUser]);

  // Auto-open add modal if requested (e.g., when adding a new board member)
  useEffect(() => {
    if (autoOpenAddModal && dataLoaded && !showAddModal) {
      setDefaultAssignmentType('primary');
      setEditingAssignment(null);
      setShowAddModal(true);
    }
  }, [autoOpenAddModal, dataLoaded]);

  const loadReferenceData = async () => {
    try {
      const [roles, leas] = await Promise.all([
        descriptorsApi.getByTypeName('Staff Role'),
        leasApi.list({ limit: 500 })
      ]);

      setRoleDescriptors(roles);

      // For superAdmin: show all organizations
      // For organizationAdmin: show only their organization and organizations from existing assignments
      if (currentUser?.role === 'superAdmin') {
        const allOrgs = leas.map(lea => ({
          id: lea.educationOrganizationId,
          name: lea.nameOfInstitution
        }));
        setOrganizations(allOrgs);
      } else {
        // Extract unique organizations from existing assignments
        const uniqueOrgs = Array.from(
          new Map(
            assignments.map(a => [a.educationOrganizationId, { id: a.educationOrganizationId, name: a.organizationName }])
          ).values()
        );
        setOrganizations(uniqueOrgs);
      }

      setDataLoaded(true);
    } catch (err) {
      console.error('Failed to load reference data:', err);
      setDataLoaded(true);
    }
  };

  // Helper to get descriptor label from ID
  const getDescriptorLabel = (descriptorId: number): string => {
    const descriptor = roleDescriptors.find(d => d.descriptorId === descriptorId);
    return descriptor?.shortDescription || `Role ${descriptorId}`;
  };

  // Separate primary and role tag assignments. Ended tags no longer count
  // (same as the Role filter), so they aren't shown and can be re-added.
  const primaryAssignments = assignments.filter(a => a.isPrimary || a.includeInDirectory);
  const roleTagAssignments = assignments.filter(
    a => !a.isPrimary && !a.includeInDirectory && a.roleDescriptorId && isCurrentAssignment(a)
  );

  // Same rule as the Role filter (backend role_tag_utils): tag rows at a
  // position's organization apply to it, shared by every position the person
  // has there; a school position also picks up tags saved at its district
  // when the person has no district position.
  const tagsForOrg = (orgId: number) => roleTagAssignments.filter(t => t.educationOrganizationId === orgId);
  const currentDirectoryOrgIds = new Set(
    primaryAssignments.filter(a => a.includeInDirectory && isCurrentAssignment(a)).map(a => a.educationOrganizationId)
  );
  const inheritedTagsFor = (position: StaffAssignment) => {
    const districtId = position.districtEducationOrganizationId;
    return districtId && !currentDirectoryOrgIds.has(districtId) ? tagsForOrg(districtId) : [];
  };
  const countCurrentPositionsAt = (orgId: number) => primaryAssignments.filter(
    a => a.educationOrganizationId === orgId && a.includeInDirectory && isCurrentAssignment(a)
  ).length;
  const shownTagOrgIds = new Set<number>();
  for (const position of primaryAssignments) {
    shownTagOrgIds.add(position.educationOrganizationId);
    if (inheritedTagsFor(position).length > 0 && position.districtEducationOrganizationId) {
      shownTagOrgIds.add(position.districtEducationOrganizationId);
    }
  }
  const otherRoleTags = roleTagAssignments.filter(t => !shownTagOrgIds.has(t.educationOrganizationId));

  const handleDeleteAssignment = async (assignmentId: number) => {
    if (!confirm('Are you sure you want to remove this assignment?')) return;

    try {
      setLoading(true);
      await staffApi.deleteAssignment(staffId, assignmentId);
      setSuccessMessage('Assignment removed successfully');
      onUpdate();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete assignment');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAssignment = async (data: StaffAssignmentCreateData) => {
    try {
      setLoading(true);
      await staffApi.addAssignment(staffId, data);
      setSuccessMessage('Assignment added successfully');
      setShowAddModal(false);
      setEditingAssignment(null);
      onUpdate();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        if (confirm(err.message)) {
          await staffApi.addAssignment(staffId, data, true);
          setSuccessMessage('Assignment added successfully');
          setShowAddModal(false);
          setEditingAssignment(null);
          onUpdate();
          setTimeout(() => setSuccessMessage(null), 3000);
          return;
        }
        return; // User cancelled
      }
      throw err; // Let the modal handle other errors
    } finally {
      setLoading(false);
    }
  };

  // The position and its tags (data.roleTagDescriptorIds) save in one request
  const handleEditAssignment = async (assignment: StaffAssignment, data: StaffAssignmentCreateData) => {
    try {
      setLoading(true);
      try {
        await staffApi.updateAssignment(staffId, assignment.assignmentId, data);
      } catch (err) {
        if (!(err instanceof ApiError && err.status === 409)) throw err; // Let the modal handle other errors
        if (!confirm(err.message)) return; // User cancelled
        await staffApi.updateAssignment(staffId, assignment.assignmentId, data, true);
      }

      setSuccessMessage('Assignment updated successfully');
      setShowAddModal(false);
      setEditingAssignment(null);
      onUpdate();
      setTimeout(() => setSuccessMessage(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <Alert color="failure" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert color="success" onDismiss={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Primary Assignments */}
      <Card>
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-semibold text-gray-700">Primary Assignments</h4>
          {canEdit && (
            <Button size="xs" onClick={() => {
              setDefaultAssignmentType('primary');
              setEditingAssignment(null);
              setShowAddModal(true);
            }}>
              <HiPlus className="mr-1 h-3 w-3" />
              Add Assignment
            </Button>
          )}
        </div>

        {primaryAssignments.length === 0 ? (
          <p className="text-gray-500 text-sm">No primary assignments</p>
        ) : (
          <div className="space-y-2">
            {primaryAssignments.map(assignment => (
              <div key={assignment.assignmentId} className="border rounded-lg p-3 bg-gray-50">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <HiBriefcase className="h-4 w-4 text-gray-400" />
                      <span className="font-medium text-gray-700">{assignment.organizationName}</span>
                      {assignment.isPrimary && (
                        <Badge color="purple" size="xs">Primary</Badge>
                      )}
                      {assignment.includeInDirectory && (
                        <Badge color="success" size="xs">In Directory</Badge>
                      )}
                    </div>
                    {assignment.positionTitle && (
                      <div className="text-base font-semibold text-gray-900 ml-6">
                        {assignment.positionTitle}
                      </div>
                    )}
                    {assignment.beginDate && (
                      <div className="mt-1 text-xs text-gray-500 ml-6">
                        {formatDateLocal(assignment.beginDate)}
                        {assignment.endDate && ` - ${formatDateLocal(assignment.endDate)}`}
                      </div>
                    )}
                    {(() => {
                      const tags = tagsForOrg(assignment.educationOrganizationId);
                      const inherited = inheritedTagsFor(assignment);
                      const hasRole = tags.length > 0 || inherited.length > 0 || !!assignment.roleDescriptorId;
                      // Skip the position's own role when a tag row repeats it
                      const showPositionRole = !!assignment.roleDescriptorId &&
                        !tags.some(t => t.roleDescriptorId === assignment.roleDescriptorId);
                      // Title-less rows (e.g. BoardMember voting rows) are managed on the
                      // Board tab, and an ended position's tags ended with it
                      const isTaggablePosition = !!assignment.positionTitle?.trim() && isCurrentAssignment(assignment);
                      return (
                        <div className="mt-2 ml-6 flex flex-wrap items-center gap-2">
                          {showPositionRole && assignment.roleDescriptorId && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-800">
                              <HiTag className="h-3 w-3" />
                              {getDescriptorLabel(assignment.roleDescriptorId)}
                            </span>
                          )}
                          {tags.map(tag => (
                            <span
                              key={tag.assignmentId}
                              className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-800"
                            >
                              <HiTag className="h-3 w-3" />
                              {getDescriptorLabel(tag.roleDescriptorId!)}
                            </span>
                          ))}
                          {inherited.map(tag => (
                            <span
                              key={tag.assignmentId}
                              title={`Saved at ${tag.organizationName}`}
                              className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700"
                            >
                              <HiTag className="h-3 w-3" />
                              {getDescriptorLabel(tag.roleDescriptorId!)}
                            </span>
                          ))}
                          {!hasRole && assignment.includeInDirectory && isTaggablePosition && (
                            <span className="inline-flex items-center gap-1 text-xs text-amber-700">
                              <HiExclamation className="h-3.5 w-3.5" />
                              No role tags, so the Role filter can't find this contact
                            </span>
                          )}
                          {canEdit && isTaggablePosition && (
                            <button
                              type="button"
                              onClick={() => {
                                setEditingAssignment(assignment);
                                setShowAddModal(true);
                              }}
                              className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
                              disabled={loading}
                            >
                              {hasRole ? 'Edit tags' : 'Add tags'}
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  {canEdit && (
                    <div className="flex space-x-1 ml-2">
                      <Button
                        size="xs"
                        color="gray"
                        onClick={() => {
                          setEditingAssignment(assignment);
                          setShowAddModal(true);
                        }}
                        disabled={loading}
                      >
                        <HiPencil className="h-3 w-3" />
                      </Button>
                      <Button
                        size="xs"
                        color="failure"
                        onClick={() => handleDeleteAssignment(assignment.assignmentId)}
                        disabled={loading}
                      >
                        <HiTrash className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Role tags at an organization with no position here */}
      {otherRoleTags.length > 0 && (
        <Card>
          <div className="flex justify-between items-center mb-4">
            <div>
              <h4 className="font-semibold text-gray-700">Other Role Tags</h4>
              <p className="text-xs text-gray-500">Tags at an organization with no position listed above</p>
            </div>
            {canEdit && (
              <Button size="xs" color="gray" onClick={() => {
                setDefaultAssignmentType('roleTag');
                setEditingAssignment(null);
                setShowAddModal(true);
              }}>
                <HiPlus className="mr-1 h-3 w-3" />
                Add Role Tag
              </Button>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {otherRoleTags.map(assignment => (
              <div
                key={assignment.assignmentId}
                className="inline-flex items-center space-x-1 bg-blue-50 text-blue-700 px-3 py-1 rounded-full"
              >
                <HiBriefcase className="h-3 w-3" />
                <span className="text-sm font-medium">
                  {assignment.organizationName}
                  {assignment.roleDescriptorId && ` - ${getDescriptorLabel(assignment.roleDescriptorId)}`}
                </span>
                {canEdit && (
                  <>
                    <button
                      onClick={() => {
                        setEditingAssignment(assignment);
                        setShowAddModal(true);
                      }}
                      className="ml-1 text-blue-500 hover:text-blue-700"
                      disabled={loading}
                      title="Edit"
                    >
                      <HiPencil className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => handleDeleteAssignment(assignment.assignmentId)}
                      className="ml-1 text-blue-500 hover:text-blue-700"
                      disabled={loading}
                      title="Delete"
                    >
                      <HiTrash className="h-3 w-3" />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      <p className="text-xs text-gray-500">
        <HiTag className="inline h-3 w-3 mr-1" />
        Role tags are standard roles, like Principal or Business Manager. They let people find a
        contact with the Role filter in Search Contacts, whatever the organization calls the position.
      </p>

      {/* Add/Edit Assignment Modal */}
      <AddAssignmentModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setEditingAssignment(null);
        }}
        onSave={editingAssignment
          ? (data) => handleEditAssignment(editingAssignment, data)
          : handleAddAssignment
        }
        assignment={editingAssignment}
        existingRoleTags={editingAssignment ? tagsForOrg(editingAssignment.educationOrganizationId) : []}
        inheritedRoleIds={editingAssignment
          ? inheritedTagsFor(editingAssignment).map(t => t.roleDescriptorId!)
          : []}
        sharedPositionCount={editingAssignment
          ? countCurrentPositionsAt(editingAssignment.educationOrganizationId)
          : 0}
        organizations={organizations}
        roleDescriptors={roleDescriptors}
        lockedOrganizationId={lockedOrganizationId}
        lockedOrganizationName={lockedOrganizationId
          ? assignments.find(a => a.educationOrganizationId === lockedOrganizationId)?.organizationName
          : undefined}
        defaultAssignmentType={editingAssignment ? undefined : defaultAssignmentType}
        defaultRoleDescriptorId={editingAssignment ? undefined : defaultRoleDescriptorId}
        prioritizeBoardRoles={prioritizeBoardRoles}
      />
    </div>
  );
};
